import React from 'react';
import { PartOrder } from 'app/models/PartOrder';
import { PrintJobFactory, PrintJobFilters } from '../PrintJobFactory';
import type { PrintJob, PrintQueue, PrintResult, IPPConfig } from '../types';
import RendererPdfService from './RendererPdfService';
import noticePathResolver, { NoticePathResolver, NoticeResolutionResult } from './NoticePathResolver';
import { ElectronPrintingApi } from './ElectronPrintingApi';
import { Workstation } from 'app/containers/OrderDetails/types';
import PickingCornerPagePrintable from './printable/PickingCornerPagePrintable';
import PickingPagePrintable from './printable/PickingPagePrintable';
import { LabelPage } from 'app/containers/OrderDetails/PackagesTab/LabelsToPrint/LabelPage';
import { OrderPage } from 'app/containers/OrderDetails/PackagesTab/LabelsToPrint/OrderPage';
import { getPrintProfileDefinition } from '../../../../shared/printing/printProfiles';
import { PickingOrderStrategy } from 'shared/printing/pickingOrder';

export interface ElectronPrintSelectionOptions {
  picking: boolean;
  labels: boolean;
  notices: boolean;
}

export interface ElectronPrintPreparation {
  jobs: PrintJob[];
  queue: PrintQueue;
  warnings: string[];
  noticeResolution?: NoticeResolutionResult;
  skippedJobs: PrintJob[];
}

export interface ElectronPrintExecutionResult {
  result: PrintResult;
  config: IPPConfig;
}

export interface ElectronPrintPipelineDependencies {
  rendererPdfService?: RendererPdfService;
  noticeResolver?: NoticePathResolver;
  printingApi?: typeof ElectronPrintingApi;
}

const DEFAULT_GROUPING: PrintQueue['groupingStrategy'] = 'document-type';

export class ElectronPrintPipeline {
  private readonly renderer: RendererPdfService;
  private readonly noticeResolver: NoticePathResolver;
  private readonly printingApi: typeof ElectronPrintingApi;

  constructor({
    rendererPdfService,
    noticeResolver = noticePathResolver,
    printingApi = ElectronPrintingApi,
  }: ElectronPrintPipelineDependencies = {}) {
    this.renderer = rendererPdfService ?? new RendererPdfService();
    this.noticeResolver = noticeResolver;
    this.printingApi = printingApi;
  }

  async prepare(
    partOrder: PartOrder,
    selection: ElectronPrintSelectionOptions,
    pickingOrderStrategy: PickingOrderStrategy,
  ): Promise<ElectronPrintPreparation> {
    const filters = this.buildFiltersFromSelection(selection);
    let allJobs = PrintJobFactory.createJobsForPartOrderWithFilters(partOrder, filters);

    // If both picking and labels are selected, build a single combined job
    if (selection.picking && selection.labels) {
      const pickingJobIndex = allJobs.findIndex(j => j.type === 'picking');
      if (pickingJobIndex >= 0) {
        const combinedContent = this.buildCombinedPickingLabelsContent(
          partOrder,
          selection,
          pickingOrderStrategy,
        );
        const labelsCount = this.countAllLabels(partOrder);
        const estimatedPickingPages = this.estimatePickingPages(partOrder);
        const includeOrderPage = partOrder.carrier?.name !== 'BPOST';

        allJobs[pickingJobIndex] = {
          ...allJobs[pickingJobIndex],
          content: combinedContent,
          metadata: {
            ...allJobs[pickingJobIndex].metadata,
            pageCount: Math.max(1, estimatedPickingPages + labelsCount + (includeOrderPage ? 1 : 0)),
            groupKey: 'picking',
            format: 'A4',
          },
        };
        // Remove separate label jobs; they are merged into picking
        allJobs = allJobs.filter(j => j.type !== 'label');
      }
    }

    if (allJobs.length === 0) {
      throw new Error('Aucun document à préparer pour cette sélection.');
    }

    const warnings: string[] = [];
    const skippedJobs: PrintJob[] = [];

    await this.attachPdfPaths(allJobs);

    const noticeResolution = await this.resolveNoticePaths(partOrder.id, allJobs);
    if (noticeResolution?.missingUrls?.length) {
      warnings.push(
        `${noticeResolution.missingUrls.length} notice(s) introuvable(s); impression sautée.`,
      );
    }

    await this.applyNoticeCoverStamp(partOrder, allJobs, warnings);

    await this.imposeAssemblyBooklets(allJobs, warnings);

    const sanitisedJobs = this.removeUnprintableJobs(allJobs, skippedJobs, warnings);

    if (sanitisedJobs.length === 0) {
      throw new Error('Aucun document prêt pour impression (tous échoués).');
    }

    const queue: PrintQueue = {
      jobs: sanitisedJobs,
      totalPages: sanitisedJobs.reduce(
        (total, job) => total + (job.metadata.pageCount ?? 0),
        0,
      ),
      groupingStrategy: DEFAULT_GROUPING,
    };

    return {
      jobs: sanitisedJobs,
      queue,
      warnings,
      noticeResolution,
      skippedJobs,
    };
  }

  async print(
    jobs: PrintJob[],
    config?: IPPConfig,
  ): Promise<ElectronPrintExecutionResult> {
    const printerConfig = config ?? this.printingApi.createDefaultConfig();
    const connection = await this.printingApi.connect(printerConfig);

    if (!connection.connected) {
      throw new Error(connection.error ?? "Connexion à l'imprimante Ricoh impossible");
    }

    const result = await this.printingApi.print({
      jobs,
      printerConfig,
    });

    return {
      result,
      config: printerConfig,
    };
  }

  private buildFiltersFromSelection(
    selection: ElectronPrintSelectionOptions,
  ): PrintJobFilters {
    return {
      includePicking: selection.picking,
      includeLabels: selection.labels,
      includeCarrierPages: false, // La file PartOrder n'imprime plus de pages transporteur
      includeNotices: selection.notices,
    };
  }

  // Build a single React tree with the same sequence as the legacy web print:
  // - Optional OrderPage (non-BPOST)
  // - For each workstation: picking pages intertwined with labels (per trolley)
  private buildCombinedPickingLabelsContent(
    partOrder: PartOrder,
    selection: ElectronPrintSelectionOptions,
    pickingOrderStrategy: PickingOrderStrategy,
  ): React.ReactNode {
    const nodes: React.ReactNode[] = [];

    const shouldRenderLabels = selection.labels && partOrder.carrier?.name !== 'BPOST';

    if (shouldRenderLabels) {
      nodes.push(<OrderPage key="order-first-page" partOrder={partOrder} firstPage={true} />);
    }

    const workstations: Workstation[] = [
      Workstation.Cabinet,
      Workstation.Corner,
      Workstation.Drawer,
      Workstation.Accessory,
    ];

    for (const ws of workstations) {
      const trolleys = (partOrder.trolleys || []).filter(t => t?.workstation === ws);
      if (!trolleys.length) continue;

      if (ws === Workstation.Corner) {
        if (selection.picking) {
          nodes.push(
            <PickingCornerPagePrintable
              key={`picking-corner-${ws}`}
              order={partOrder}
              trolleys={trolleys as any}
              workstation={ws}
            />,
          );
        }
        // Corner labels rendered after picking
        if (shouldRenderLabels && !partOrder.direct && partOrder.carrier?.name !== 'B-POST') {
          for (const trolley of trolleys as any[]) {
            for (const pkg of trolley.packages || []) {
              nodes.push(
                <LabelPage
                  key={`label-${ws}-${pkg.id}`}
                  partOrder={partOrder as any}
                  packageOrder={pkg}
                />,
              );
            }
          }
        }
      } else {
        for (const trolley of trolleys as any[]) {
          if (selection.picking && (trolley.production?.length ?? 0) > 0) {
            const drawerId = trolley.drawer as number | undefined;
            const drawerPieces = drawerId
              ? (partOrder.products || []).find(p => p?.id === drawerId)?.production || []
              : [];

            nodes.push(
              <PickingPagePrintable
                key={`picking-${ws}-${trolley.id}`}
                order={partOrder as any}
                pieces={trolley.production || []}
                workstation={ws}
                trolleyName={trolley.name}
                drawerPieces={drawerPieces as any}
                pickingOrderStrategy={pickingOrderStrategy}
              />,
            );
          }
          if (shouldRenderLabels) {
            for (const pkg of trolley.packages || []) {
              nodes.push(
                <LabelPage
                  key={`label-${ws}-${trolley.id}-${pkg.id}`}
                  partOrder={partOrder as any}
                  packageOrder={pkg}
                />,
              );
            }
          }
        }
      }
    }

    return <>{nodes}</>;
  }

  private countAllLabels(partOrder: PartOrder): number {
    if (!partOrder?.trolleys?.length) return 0;
    let count = 0;
    for (const trolley of partOrder.trolleys as any[]) {
      if (Array.isArray(trolley?.packages)) count += trolley.packages.length;
    }
    return count;
  }

  private estimatePickingPages(partOrder: PartOrder): number {
    const relevantTrolleys = (partOrder.trolleys || []).filter(t => (t as any)?.production?.length > 0);
    return Math.max(1, Math.ceil(relevantTrolleys.length / 2));
  }

  private async attachPdfPaths(jobs: PrintJob[]): Promise<void> {
    const renderableJobs = jobs.filter(
      job => job.type !== 'assembly',
    );

    if (renderableJobs.length === 0) {
      return;
    }

    const pdfPaths = await this.renderer.renderJobsToPdfs(renderableJobs);

    for (const job of renderableJobs) {
      const path = pdfPaths[job.id];
      if (path) {
        job.electronPath = path;
      }
    }
  }

  private async resolveNoticePaths(
    partOrderId: number,
    jobs: PrintJob[],
  ): Promise<NoticeResolutionResult | undefined> {
    const assemblyJobs = jobs.filter(job => job.type === 'assembly');
    if (assemblyJobs.length === 0) {
      return undefined;
    }

    const instructionUrls = this.collectInstructionUrls(assemblyJobs);
    if (instructionUrls.length === 0) {
      return undefined;
    }

    const resolution = await this.noticeResolver.resolvePaths(
      partOrderId,
      instructionUrls,
    );

    if (Object.keys(resolution.resolvedPaths).length > 0) {
      PrintJobFactory.attachAssemblyPaths(jobs, resolution.resolvedPaths);
    }

    return resolution;
  }

  private async applyNoticeCoverStamp(
    partOrder: PartOrder,
    jobs: PrintJob[],
    warnings: string[],
  ): Promise<void> {
    if (!this.printingApi.supportsPdfAnnotation()) {
      return;
    }

    const annotatableJobs = jobs.filter(
      job => job.type === 'assembly' && job.electronPath,
    );

    if (annotatableJobs.length === 0) {
      return;
    }

    const total = annotatableJobs.length;
    const customerName = partOrder.customerName ?? '';
    const orderId = partOrder.order?.id ?? partOrder.id;

    for (let index = 0; index < annotatableJobs.length; index++) {
      const job = annotatableJobs[index];
      if (!job.electronPath) {
        continue;
      }

      try {
        const result = await this.printingApi.annotatePdfCover(job.electronPath, {
          customerName,
          orderId,
          index: index + 1,
          total,
        });

        if (result?.path) {
          job.electronPath = result.path;
        }
      } catch (error) {
        const product = this.getProductForAssemblyJob(job);
        const label = product?.name || `produit #${product?.id ?? '?'}`;
        const message = error instanceof Error ? error.message : String(error);
        warnings.push(
          `Annotation couverture notice échouée pour ${label}: ${message}. Notice originale utilisée.`,
        );
      }
    }
  }

  private async imposeAssemblyBooklets(jobs: PrintJob[], warnings: string[]): Promise<void> {
    const assemblyJobs = jobs.filter(
      job => {
        if (job.type !== 'assembly' || !job.metadata.profileId) return false;
        return Boolean(getPrintProfileDefinition(job.metadata.profileId).booklet);
      },
    );

    if (assemblyJobs.length === 0) {
      return;
    }

    for (const job of assemblyJobs) {
      const profileId = job.metadata.profileId;
      if (!job.electronPath || !profileId) {
        continue;
      }

      try {
        const result = await this.printingApi.imposeBooklet(job.electronPath, job, {
          // Defaults handled in main process; keep placeholder for future UI overrides
        });

        job.electronPath = result.path;
        job.metadata.pageCount = result.pageCount;
        job.metadata.format =
          result.sheetFormat ??
          getPrintProfileDefinition(profileId).sheetFormat;
        job.metadata.orientation = 'landscape';
        job.metadata.margin = 'none';

        if (result.sourcePageCount % 4 !== 0) {
          const product = this.getProductForAssemblyJob(job);
          const label = product?.name || `produit #${product?.id ?? '?'}`;
          const paddedTotal = result.pageCount * 2;
          warnings.push(
            `Notice ${label}: ${result.sourcePageCount} page(s) -> complétée à ${paddedTotal} page(s) pour imposition livret.`,
          );
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        job.electronPath = undefined;
        warnings.push(
          `Booklet imposition failed for ${job.id}: ${message}. The notice was skipped.`,
        );
      }
    }
  }

  private collectInstructionUrls(jobs: PrintJob[]): string[] {
    const urls = new Set<string>();

    for (const job of jobs) {
      const product = this.getProductForAssemblyJob(job);
      const url = (product as any)?.instructions ?? (product as any)?.assemblyInstructions;
      if (typeof url === 'string' && url.trim().length > 0) {
        urls.add(url);
      }
    }

    return Array.from(urls);
  }

  private getProductForAssemblyJob(job: PrintJob) {
    if (job.type !== 'assembly') {
      return undefined;
    }

    const productId =
      job.metadata.productId ?? this.parseProductIdFromJobId(job.id);
    return job.metadata.originalPartOrder.products?.find(
      product => product.id === productId,
    );
  }

  private parseProductIdFromJobId(jobId: string): number | undefined {
    const numericSegments = jobId
      .split('-')
      .filter(segment => /^[0-9]+$/.test(segment));

    if (numericSegments.length >= 2) {
      return Number(numericSegments[1]);
    }

    return undefined;
  }

  private removeUnprintableJobs(
    jobs: PrintJob[],
    skippedJobs: PrintJob[],
    warnings: string[],
  ): PrintJob[] {
    const printable: PrintJob[] = [];

    for (const job of jobs) {
      if (!job.electronPath) {
        skippedJobs.push(job);

        if (job.type === 'assembly') {
          const product = this.getProductForAssemblyJob(job);
          const label = product?.name || `produit #${product?.id ?? '?'}`;
          warnings.push(
            `Notice non disponible pour ${label} — impression ignorée.`,
          );
        } else {
          warnings.push(
            `Document ${job.id} sans PDF généré — impression ignorée.`,
          );
        }
        continue;
      }

      printable.push(job);
    }

    return printable;
  }
}

const electronPrintPipeline = new ElectronPrintPipeline();

export default electronPrintPipeline;
